import { useEffect, useRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Avatar, STATE_LABELS } from './Avatar'
import { FlyingFigure } from './FlyingFigure'
import { PoweredBy } from './PoweredBy'
import type { AvatarState } from '../state/chatStore'
import type { IntroChatNachricht } from '../lib/api'

/**
 * Der Chat auf dem Intro-Bildschirm — gebaut wie der gewöhnliche K4ia-Chat
 * (ChatView): Kopfzeile mit Figur und Status, Sprechblasen, die fliegende
 * Figur, während K4ia nachdenkt. Nur größer, für ein paar Meter Abstand, und
 * ohne Eingabefeld: Gefragt wird am Handy, das unten steht es. Fotos vom
 * Handy stehen groß über der Frage, mit der sie kamen.
 *
 * Gescrollt wird nie von Hand — der Verlauf läuft von selbst mit, wie im
 * Chat am Handy, nur ohne Bildlaufleiste.
 */
export function IntroChatAnsicht({
  nachrichten,
  avatarZustand,
  pausiert,
}: {
  nachrichten: IntroChatNachricht[]
  avatarZustand: AvatarState
  pausiert: boolean
}) {
  const reduceMotion = useReducedMotion()
  const liste = useRef<HTMLDivElement>(null)
  const denkt = nachrichten.at(-1)?.role === 'user'

  useEffect(() => {
    liste.current?.scrollTo({
      top: liste.current.scrollHeight,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
  }, [nachrichten.length, nachrichten.at(-1)?.content, reduceMotion])

  const status = pausiert
    ? 'Pausiert'
    : avatarZustand === 'idle'
      ? 'Assistentin im J4F VisionLab'
      : STATE_LABELS[avatarZustand]

  return (
    <div className="flex h-screen flex-col bg-bg">
      <header className="glass z-20 flex items-center gap-6 px-10 py-6">
        <Avatar state={avatarZustand} size={88} showLabel={false} />
        <div className="min-w-0 flex-1">
          {/* Brooklyn Bold über die h1-Regel in global.css (ADR 0011). */}
          <h1 className="text-5xl leading-tight">Hallo! Ich bin K4ia.</h1>
          <p className="truncate text-lg text-text-muted" aria-live="polite">
            {status}
          </p>
        </div>
        <span className="rounded-full bg-surface-2 px-5 py-2 text-base text-text-muted">
          Gesteuert vom Handy
        </span>
      </header>

      <div
        ref={liste}
        role="log"
        aria-label="Gesprächsverlauf"
        className="flex-1 overflow-y-auto px-10 pb-8 pt-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
          {nachrichten.length === 0 && (
            <div className="mt-12 flex flex-col items-center text-center">
              <Avatar state={avatarZustand} size={180} showLabel={false} />
              <h2 className="mt-8 text-5xl">Wie kann ich dir helfen?</h2>
              <p className="mt-4 text-2xl text-text-muted">
                Frag mich am Handy — tippen oder „Sprechen“ antippen.
              </p>
            </div>
          )}

          {nachrichten.map((n) => (
            <motion.div
              key={n.id}
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`flex ${n.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {n.role === 'user' ? (
                <div className="flex max-w-[75%] flex-col items-end gap-3">
                  {n.foto && (
                    <img
                      src={n.foto}
                      alt="Foto vom Handy"
                      // Erst geladen ist es hoch — dann noch einmal ans Ende scrollen.
                      onLoad={() => liste.current?.scrollTo({ top: liste.current.scrollHeight })}
                      className="max-h-[45vh] w-auto rounded-[2rem] rounded-br-lg object-contain shadow-[var(--shadow-card)]"
                    />
                  )}
                  <p className="rounded-[2rem] rounded-br-lg bg-accent px-7 py-4 text-2xl leading-snug text-on-accent">
                    {n.content}
                  </p>
                </div>
              ) : (
                <div className="flex max-w-[85%] flex-col items-start gap-4">
                  <div className="markdown-body break-words rounded-[2rem] rounded-bl-lg bg-surface px-8 py-6 text-2xl leading-relaxed text-text shadow-[var(--shadow-card)] [&_a]:underline [&_a]:decoration-dotted [&_li]:ml-7 [&_li]:list-disc [&_p+p]:mt-4">
                    <ReactMarkdown>{n.content}</ReactMarkdown>
                  </div>
                  <PoweredBy showcase={n.partner} gross />
                </div>
              )}
            </motion.div>
          ))}

          {denkt && (
            <div className="flex justify-start">
              <div className="rounded-[2rem] rounded-bl-lg bg-surface px-8 py-6 shadow-[var(--shadow-card)]">
                {/* Anderthalbfach, mit passendem Platz — scale allein vergrößert nur das Bild. */}
                <div style={{ width: 168, height: 45 }}>
                  <div className="origin-top-left scale-150">
                    <FlyingFigure />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Wo sonst das Eingabefeld steht: der Hinweis, wo gefragt wird. */}
      <div className="glass mx-10 mb-6 flex items-center justify-center gap-3 rounded-full px-8 py-4 shadow-float">
        <span aria-hidden className="text-2xl">
          📱
        </span>
        <p className="text-xl text-text-muted">Fragen kommen vom Handy — getippt oder gesprochen</p>
      </div>
    </div>
  )
}
